import { Alert, Button, Divider, Group, Loader, Stack, Text, Title, Tooltip } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { modals } from "@mantine/modals";
import { IconInfoCircle, IconInfoTriangle, IconSend } from "@tabler/icons-react";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import { Suspense } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { PropertyBadge } from "shared/components/global/PropertyBadge";
import { Section, SectionProvider } from "shared/components/global/Section";
import {
    ComposeFormProvider,
    ComposeFormState,
    composeFormStateToDraftInfoInput,
    useComposeFormContext,
} from "shared/components/message/compose/context";
import { useMessageActions } from "shared/components/message/useMessageActions";
import { NestedTopicTree } from "shared/components/organization/topics/TopicNode";
import { useCustomer, useIsInBlackoutPeriod } from "shared/graphql/cust";
import { features } from "shared/graphql/features";
import { topicsQueryOptions } from "shared/graphql/topics";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { createRandomStr, to } from "shared/utils/fns";
import { maybe, some } from "shared/utils/maybe";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";
import { Cm, DraftInfoInput, MessageSeverity } from "src/gql/graphql";

export function useComposeFormActions(form: UseFormReturnType<ComposeFormState>) {
    const modalId = createRandomStr();
    const nav = useNavigate();

    return {
        openSendModal(opts: { getMessage: () => Promise<{ id: string }> }) {
            modals.open({
                modalId,
                title: <Title order={3}>Send Message</Title>,
                children: (
                    <ComposeFormProvider form={form}>
                        <Suspense fallback={<Loader />}>
                            <SendModal
                                getMessage={opts.getMessage}
                                onClose={() => modals.close(modalId)}
                                onScheduled={({ id }) => {
                                    form.resetDirty();
                                    void nav({ to: "/messages/scheduled" });
                                    window.interop?.draftDidSchedule?.(id);
                                    modals.close(modalId);
                                }}
                                onSuccess={({ id }) => {
                                    form.resetDirty();
                                    modals.close(modalId);
                                    notify.show.success("Message sent!");
                                    window.interop?.draftDidSend?.(id);
                                    void nav({ to: "/messages/$id", params: { id } });
                                }}
                            />
                        </Suspense>
                    </ComposeFormProvider>
                ),
            });
        },
    };
}

const OptInWarning = () => {
    const hasOptIn = features.enabled("HAS_WEB_OPT_IN");
    const hasCmOverrides = features.enabled("HAS_CM_DELIVERY_OVERRIDES");
    const form = useComposeFormContext();
    const topics = useSuspenseQuery(topicsQueryOptions.theWholeTree());

    const { audience, options, severity } = form.getValues();

    function hasRequiredTopic(node: NestedTopicTree): boolean {
        if (node.isRequired && audience.topics.some((t) => t.id == node.id)) return true;
        return node.children?.some((child) => hasRequiredTopic(child)) ?? false;
    }

    if (
        !hasOptIn ||
        !hasCmOverrides ||
        severity != MessageSeverity.Informational ||
        options.some((op) => op.method == Cm.Sms) ||
        !hasRequiredTopic(topics.data.rootTopic)
    )
        return null;

    return (
        <Alert title="Public users may be excluded" color="red" icon={<IconInfoCircle />}>
            Users who signed up only with an SMS number have been excluded. To include them, select SMS as a delivery option.
        </Alert>
    );
};

const SendModal = (props: {
    getMessage: () => Promise<{ id: string }>;
    onClose: () => void;
    onSuccess: (message: { id: string }) => void;
    onScheduled: (message: { id: string }) => void;
}) => {
    const {
        data: { blackoutPeriod },
    } = useCustomer();
    const { isInBlackoutPeriod } = useIsInBlackoutPeriod(+dayjs().set("second", 0).set("millisecond", 0).toDate());
    const actions = useMessageActions();
    const form = useComposeFormContext();
    const { data } = useSuspenseQuery(queries.projectedDelivery({ info: composeFormStateToDraftInfoInput.parse(form.getValues()) }));

    const publish = useGraphqlMutation(muts.publish);

    const endOfBlackoutPeriod = maybe(blackoutPeriod)
        ?.take(({ end }) =>
            some(
                dayjs()
                    .set("hours", Math.floor(end / 60))
                    .set("minutes", Math.floor(end % 60)),
            ).take((it) => (+it < Date.now() ? it.add(1, "day") : it)),
        )
        .toDate();

    function handleSend() {
        props
            .getMessage()
            .then(({ id }) => publish.mutateAsync({ id, expireAt: null }))
            .then(({ sendMessage: { id } }) => props.onSuccess({ id }))
            .catch(notify.catch);
    }

    return (
        <Stack>
            <Text>You are about to send a message. Are you sure you want to continue?</Text>
            <SectionProvider>
                <Section label={"Recipients"}>
                    <Group gap={"xs"}>
                        <PropertyBadge title="Users Notified">{data.uniqueUserCount}</PropertyBadge>
                        <PropertyBadge title="Total Deliveries">{data.totalDeliveryCount}</PropertyBadge>
                        {data.totalEmails && <PropertyBadge title="Emails">{data.totalEmails}</PropertyBadge>}
                        {!!data.totalTexts && <PropertyBadge title="Texts (SMS)">{data.totalTexts}</PropertyBadge>}
                        {!!data.totalPhoneCalls && <PropertyBadge title="Phone Calls">{data.totalPhoneCalls}</PropertyBadge>}
                        {!!(data.totalAndroid + data.totalIOS) && (
                            <PropertyBadge title="Mobile Push">{data.totalAndroid + data.totalIOS}</PropertyBadge>
                        )}
                    </Group>
                </Section>
            </SectionProvider>
            {isInBlackoutPeriod && (
                <Alert variant="light" icon={<IconInfoTriangle size={20} />} title="Blackout Period">
                    The current time falls withing your organization's blackout period.
                </Alert>
            )}
            <OptInWarning />
            <Divider />

            {isInBlackoutPeriod ? (
                <Group justify="space-between">
                    <Tooltip label="Message send requires at least one recipient" disabled={data.totalDeliveryCount != 0}>
                        <Button
                            variant="transparent"
                            size="compact-sm"
                            td="underline"
                            disabled={!publish.isIdle || data.totalDeliveryCount == 0}
                            onClick={handleSend}
                        >
                            Send Anyway
                        </Button>
                    </Tooltip>
                    <Group>
                        <Button variant="default" onClick={props.onClose}>
                            Cancel
                        </Button>

                        <Tooltip label="Message send requires at least one recipient" disabled={data.totalDeliveryCount != 0}>
                            <Button
                                disabled={data.totalDeliveryCount == 0}
                                onClick={() =>
                                    void actions
                                        .openScheduleDeliveryModal({
                                            getMessage: props.getMessage,
                                            initialValue: endOfBlackoutPeriod,
                                        })
                                        .then(props.onScheduled)
                                        .then(props.onClose)
                                }
                            >
                                Schedule Instead
                            </Button>
                        </Tooltip>
                    </Group>
                </Group>
            ) : (
                <Group justify="flex-end">
                    <Button variant="default" onClick={props.onClose}>
                        Cancel
                    </Button>
                    <Tooltip label="Message send requires at least one recipient" disabled={data.totalDeliveryCount != 0}>
                        <Button
                            disabled={data.totalDeliveryCount == 0}
                            loading={!publish.isIdle}
                            leftSection={<IconSend size={16} />}
                            onClick={handleSend}
                        >
                            Send
                        </Button>
                    </Tooltip>
                </Group>
            )}
        </Stack>
    );
};

const queries = {
    projectedDelivery: ({ info }: { info: DraftInfoInput }) =>
        queryOptions({
            staleTime: 0, // don't cache
            queryKey: qk("compose", "projected"),
            queryFn: () =>
                httpPostGraphql(
                    graphql(`
                        query SendPanelProjectedDelivery($info: DraftInfoInput!) {
                            deliveryProjection(info: $info) {
                                uniqueUserCount
                                totalDeliveryCount
                                totalEmails: deliveryCount(cm: EMAIL)
                                totalPhoneCalls: deliveryCount(cm: PHONE)
                                totalTexts: deliveryCount(cm: SMS)
                                totalAndroid: deliveryCount(cm: ANDROID)
                                totalIOS: deliveryCount(cm: IOS)
                            }
                        }
                    `),
                    { info },
                ),
            select: to("deliveryProjection"),
        }),
};

const muts = {
    publish: graphql(`
        mutation MessageActionSendMessage($id: UUID!, $expireAt: Instant) {
            sendMessage(id: $id, expireAt: $expireAt) {
                id
            }
        }
    `),
};
